import * as React from 'react';
import { FixMeLater } from '../../utils';
import { STATIC_STYLES, useDynamicStyles } from './Skeleton.styles';
import { Styler, StylerBaseProps } from '../Styler';
import { useComponentDefaultProps } from '../../theming';
import { SkeletonVariant } from './Skeleton.types';

export interface SkeletonProps extends StylerBaseProps {
    /**
     * The `border-radius` css property. Ignored when variant="circle".
     * @default  0
     */
    radius?: number | string;

    /**
     * The type of content that will be represented by the skeleton.
     * @default  rectangle
     */
    variant?: SkeletonVariant;
}

const defaultProps: Partial<SkeletonProps> = {
    variant: 'rectangle',
};

/**
 * A skeleton serves as a placeholder for content that is loading. Multiple skeletons can be combined to create a visual representation
 * of the pending UI. Using skeletons can enhance perceived performance and reduce user frustration during load times.
 *
 * Try to integrate skeletons directly into your components. This helps keep the layout of the skeleton and the component in sync.
 */
export const Skeleton: React.ForwardRefExoticComponent<SkeletonProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function Skeleton(props: SkeletonProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            h,
            w,
            maw,
            radius,
            variant = 'rectangle',
            ...other
        } = useComponentDefaultProps('Skeleton', defaultProps, props);

        // A circle must have equal width and height
        const width = variant === 'circle' ? h : w;

        const withChildren = Boolean(other.children);

        const hasWidth = Boolean(w) || Boolean(maw);

        const cssVariables = useDynamicStyles(variant, radius, hasWidth);

        return (
            <Styler
                ref={ref}
                style={cssVariables}
                baseStyles={STATIC_STYLES}
                h={h}
                w={width}
                maw={maw}
                data-variant={variant}
                data-with-children={withChildren || undefined}
                {...other}
            />
        );
    });
